import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { Component, OnInit, Input, AfterViewInit, ViewChild, ElementRef, EventEmitter, Output } from '@angular/core';
import { KYMW00Model } from '../../kymw00/kymw00.model';
import { PlessViewUtilsSub } from '../../kymw00/pless-view-utils-sub.service';

/**
 * Pless用カスタムコンポーネント
 * 空の場合に設定した背景色に自動変更する
 *
 * @export NotNullTextAreaComponent
 * @class NotNullTextAreaComponent
 */
@Component({
  selector: 'app-not-null-text-area',
  templateUrl: './not-null-text-area.component.html',
  styleUrls: ['./not-null-text-area.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: NotNullTextAreaComponent,
      multi: true,
    },
  ]
})
export class NotNullTextAreaComponent implements OnInit, AfterViewInit, ControlValueAccessor {

  @ViewChild('textarea', { static: true }) textarea: ElementRef;

  /** 強制活性モード判定 */
  @Input() public forcedEnabledMode: string = null;

  /** 位置調整用のパラメーター */
  // @Input() public left: number = <number>0;
  // @Input() public top: number = <number>0;
  @Input() public width: number | string;
  @Input() public height: number | string;

  @Input() public subid: string = null;

  /** 親から値を受け取り入力欄に反映も可能 */
  @Input() public text: string = <string>'';

  /** 親から値を受け取り、色指定 */
  @Input() public nullColor: string = null;                                     // テキスト情報がない場合の背景色
  @Input() public notNullColor: string = null;                                  // テキスト情報がある場合の背景色
  @Input() public useDisabledBgColor: boolean = <boolean>false;                 // 非活性の場合の背景色を使用するかどうか

  /** 親から値を受け取り、最大文字数 maxChars→maxlength*/
  @Input() public maxChars: number = null;
  /** 親から値を受け取り、制限 */
  @Input() public restrict: string = null;
  /** 親から値を受け取り、全角のみ入力可とする */
  @Input() public fullOnly: boolean = <boolean>false;
  /** 親から値を受け取り、半角のみ入力可とする */
  @Input() public halfOnly: boolean = <boolean>false;
  /** 親から値を受け取り、改行の入力を許可する */
  @Input() public allowMultiLine: boolean = <boolean>true;
  @Input() toUpperCase: boolean = <boolean>false;
  /** 親から値を受け取り、活性化する */
  @Input() public editable: boolean = <boolean>true;
  /** 親から値を受け取り、タブをタブ順序に含める */
  @Input() tabEnabled: boolean = <boolean>true;
  /** 親から値を受け取り、タブ順序 */
  @Input() tabindex: number = null;
  /** 親から値を受け取り、縦スクロールの表示設定 */
  @Input() public verticalScrollPolicy: string = <string>'auto';
  /** 親から値を受け取り、横スクロールの表示設定 */
  @Input() public horizontalScrollPolicy: string = <string>'auto';
  /** 文字数ラベル表示フラグ */
  @Input() public displayCountField: boolean = <boolean>true;
  @Input() counterWidth: string;
  // 項番2231 START 障害管理NO.U01386対応
  /** 幅100%フラグ */
  @Input() fullWidth: boolean = <boolean>false;
  // 項番2231 END 障害管理NO.U01386対応

  @Input() public disabled: boolean = <boolean>false;

  @Output() public blur: EventEmitter<any> = new EventEmitter<any>();

  /** focusのイベント通知 */
  @Output() focus: EventEmitter<any> = new EventEmitter<any>();

  /** カウント */
  public count: string = null;

  /** 背景色 */
  public bg: string = <string>'white';

  /** カーソル位置 */
  private pos: number = <number>0;


  public onChange(obj: any): void { }
  public onTouched(): void { }
  public writeValue(text: string): void {
    if (text !== this.text) {
      this.text = text;
    }
  }
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Creates an instance of NotNullTextAreaComponent.
   * @param {ElementRef} elementRef
   * @memberof NotNullTextAreaComponent
   */
  constructor(
    public elementRef: ElementRef,
  ) {
  }

  /**
   * 初期処理
   *
   * @memberof NotNullTextAreaComponent
   */
  ngOnInit(): void {
    // 移植メソッドを実行する
    this.complete();
    this.textarea.nativeElement.setAttribute('id', this.subid + '_sub');
    this.textarea.nativeElement.setAttribute('forcedEnabledMode', this.forcedEnabledMode);
    if (this.displayCountField && this.counterWidth && !this.width) {
      this.width = `calc(100% - ${this.counterWidth}`;
    }
  }

  /**
   * ロード完了時
   *
   * @memberof NotNullTextAreaComponent
   */
  ngAfterViewInit(): void {
  }

  /**
   * 表示処理終了後の処理
   *
   * @private
   * @memberof NotNullTextAreaComponent
   */
  private complete(): void {
    this.resetBgColor(); // 背景色のリセット
    if (this.displayCountField) {
      this.redraw();
    }
  }

  /**
   * テキスト内容変更後処理 html側から(change)でバインド
   *
   */
  public textChanged(): void {
    this.resetBgColor();
    if (this.displayCountField) {
      this.redraw();
    }
  }

  /**
   * テキスト内容に応じて、背景色を変更する
   */
  private resetBgColor(): void {
    if (PlessViewUtilsSub.isEmpty(this.text)) {
      this.setStyle(this.bg, this.nullColor);
    } else {
      this.setStyle(this.bg, this.notNullColor ?? this.nullColor);
    }
    if ((this.disabled || this.textarea.nativeElement.disabled) && this.useDisabledBgColor) {
      this.setStyle(this.bg, KYMW00Model.DISABLED_BG_COLOR);
    }
  }

  /**
   * ラベル情報の再描画
   */
  private redraw(): void {
    if (this.displayCountField) {
      this.count = this.text.length.toString().padStart(this.maxChars.toString().length, '0') + '/' + this.maxChars;
    }
  }

  /**
   * スタイル設定
   *
   * @private
   * @param {string} bg
   * @param {string} color
   * @memberof NotNullTextAreaComponent
   */
  private setStyle(bg: string, color: string): void {
    bg = color;
    this.bg = bg;
  }

  /**
   * 縦スクロール表示の判定
   */
  public setVerticalScrollPolicy(): string {
    if (this.verticalScrollPolicy) {
      if (this.verticalScrollPolicy === 'auto') {
        return 'auto';
      } else if (this.verticalScrollPolicy === 'on') {
        return 'scroll';
      } else if (this.verticalScrollPolicy === 'off') {
        return 'hidden';
      } else {
        return null;
      }
    }
    return null;
  }

  /**
   * 横スクロール表示の判定
   */
  public setHorizontalScrollPolicy(): string {
    if (this.horizontalScrollPolicy) {
      if (this.horizontalScrollPolicy === 'auto') {
        return 'auto';
      } else if (this.horizontalScrollPolicy === 'on') {
        return 'scroll';
      } else if (this.horizontalScrollPolicy === 'off') {
        return 'hidden';
      } else {
        return null;
      }
    }
    return null;
  }

  /**
   * フォーカスインイベント処理
   *
   * @param {Event} event
   * @memberof NotNullTextAreaComponent
   */
  public onFocusIn(): void {
    // カーソル位置を退避
    this.textarea.nativeElement.setSelectionRange(this.pos, this.pos);
  }

  /**
   * フォーカスアウトイベント処理
   *
   * @param {Event} event
   * @memberof NotNullTextAreaComponent
   */
  public onFocusOut(): void {
    // カーソル位置を復元
    this.pos = this.textarea.nativeElement.selectionStart;
  }

  public setFocus() {
    this.textarea.nativeElement.focus();
  }

}
